import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useEstablishment } from '@/contexts/EstablishmentContext';
import { Reservation } from '@/types/table';
import { CustomerData } from '@/types/client';
import { aggregateClientData, CustomerNotesMap } from '@/lib/clientUtils';

interface UseClientsWithNotesOptions {
  reservations: Reservation[];
  enabled?: boolean;
}

// Reservation stats per customer fetched directly from DB (no date window limit)
interface CustomerReservationStats {
  totalReservations: number;
  arrivedReservations: number;
  totalPersons: number;
  lastReservationDate: string | null;
  firstReservationDate: string | null;
}

export function useClientsWithNotes({ reservations, enabled = true }: UseClientsWithNotesOptions) {
  const { establishmentId } = useEstablishment();
  const [customerNotesMap, setCustomerNotesMap] = useState<CustomerNotesMap>({});
  const [customerStatsMap, setCustomerStatsMap] = useState<Record<string, CustomerReservationStats>>({});
  const [loading, setLoading] = useState(true);

  const fetchCustomerNotes = useCallback(async () => {
    if (!establishmentId || !enabled) {
      setLoading(false);
      return;
    }

    try {
      const notesMap: CustomerNotesMap = {};
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('customers')
          .select('id, email, first_name, last_name, phone, notes, marketing_consent, sms_consent, marketing_consent_at, sms_consent_at, civility, country, guest_status, review_email_consent, review_email_consent_at, review_sms_consent, review_sms_consent_at, company, professional_email, professional_phone, birthdate, address, city, zip, lang, has_no_show, is_blacklisted, allergies_notes, allergies_tags, guest_tags, created_at, imported_total_reservations, imported_arrived_reservations, imported_total_persons')
          .eq('establishment_id', establishmentId)
          .range(from, from + PAGE_SIZE - 1);

        if (error) {
          console.error('[useClientsWithNotes] Error fetching notes:', error);
          return;
        }

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        data.forEach(customer => {
          const key = customer.email
            ? customer.email.toLowerCase()
            : `__no_email__${customer.id}`;

          notesMap[key] = {
            id: customer.id,
            firstName: customer.first_name,
            lastName: customer.last_name,
            phone: customer.phone,
            notes: customer.notes,
            marketingConsent: customer.marketing_consent ?? false,
            smsConsent: customer.sms_consent ?? false,
            marketingConsentAt: customer.marketing_consent_at ?? null,
            smsConsentAt: customer.sms_consent_at ?? null,
            civility: customer.civility ?? null,
            country: customer.country ?? null,
            guestStatus: customer.guest_status ?? null,
            reviewEmailConsent: customer.review_email_consent ?? false,
            reviewEmailConsentAt: customer.review_email_consent_at ?? null,
            reviewSmsConsent: customer.review_sms_consent ?? false,
            reviewSmsConsentAt: customer.review_sms_consent_at ?? null,
            company: customer.company ?? null,
            professionalEmail: customer.professional_email ?? null,
            professionalPhone: customer.professional_phone ?? null,
            birthdate: customer.birthdate ?? null,
            address: customer.address ?? null,
            city: customer.city ?? null,
            zip: customer.zip ?? null,
            lang: customer.lang ?? null,
            hasNoShow: customer.has_no_show ?? false,
            isBlacklisted: customer.is_blacklisted ?? false,
            allergiesNotes: customer.allergies_notes ?? null,
            allergiesTags: customer.allergies_tags ?? [],
            guestTags: customer.guest_tags ?? [],
            createdAt: customer.created_at ?? null,
            importedTotalReservations: (customer as any).imported_total_reservations ?? 0,
            importedArrivedReservations: (customer as any).imported_arrived_reservations ?? 0,
            importedTotalPersons: (customer as any).imported_total_persons ?? 0,
          };
      });

        from += PAGE_SIZE;
        if (data.length < PAGE_SIZE) {
          hasMore = false;
        }
      }

      setCustomerNotesMap(notesMap);
    } catch (err) {
      console.error('[useClientsWithNotes] Exception:', err);
    } finally {
      setLoading(false);
    }
  }, [establishmentId, enabled]);

  // Fetch reservation stats per customer directly from DB (no date window limitation)
  const fetchCustomerStats = useCallback(async () => {
    if (!establishmentId || !enabled) return;

    try {
      const statsMap: Record<string, CustomerReservationStats> = {};
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('reservations')
          .select('customer_id, status, number_of_people, number_of_children, date')
          .eq('establishment_id', establishmentId)
          .neq('status', 'annulé')
          .range(from, from + PAGE_SIZE - 1);

        if (error) {
          console.error('[useClientsWithNotes] Error fetching reservation stats:', error);
          return;
        }

        if (!data || data.length === 0) {
          hasMore = false;
          break;
        }

        data.forEach((r: any) => {
          const cid = r.customer_id;
          if (!cid) return;
          
          if (!statsMap[cid]) {
            statsMap[cid] = {
              totalReservations: 0,
              arrivedReservations: 0,
              totalPersons: 0,
              lastReservationDate: null,
              firstReservationDate: null,
            };
          }
          
          const stats = statsMap[cid];
          stats.totalReservations++;
          if (r.status === 'arrivé') {
            stats.arrivedReservations++;
          }
          stats.totalPersons += (r.number_of_people || 0) + (r.number_of_children || 0);
          
          if (!stats.lastReservationDate || r.date > stats.lastReservationDate) {
            stats.lastReservationDate = r.date;
          }
          if (!stats.firstReservationDate || r.date < stats.firstReservationDate) {
            stats.firstReservationDate = r.date;
          }
        });

        from += PAGE_SIZE;
        if (data.length < PAGE_SIZE) {
          hasMore = false;
        }
      }

      setCustomerStatsMap(statsMap);
    } catch (err) {
      console.error('[useClientsWithNotes] Exception fetching stats:', err);
    }
  }, [establishmentId, enabled]);

  useEffect(() => {
    fetchCustomerNotes();
    fetchCustomerStats();
  }, [fetchCustomerNotes, fetchCustomerStats]);

  // Aggregate clients with notes from the database, then override counts with DB stats
  const clients = useMemo(() => {
    const baseClients = aggregateClientData(reservations, customerNotesMap);
    
    // Override reservation counts with accurate DB-sourced stats
    if (Object.keys(customerStatsMap).length > 0) {
      baseClients.forEach(client => {
        if (client.id && customerStatsMap[client.id]) {
          const dbStats = customerStatsMap[client.id];
          // Use DB stats + imported counts for total accuracy
          const importedData = client.email 
            ? customerNotesMap[client.email.toLowerCase()] 
            : null;
          
          client.totalReservations = dbStats.totalReservations + (importedData?.importedTotalReservations || 0);
          client.arrivedReservations = dbStats.arrivedReservations + (importedData?.importedArrivedReservations || 0);
          client.totalPersons = dbStats.totalPersons + (importedData?.importedTotalPersons || 0);
          
          if (dbStats.lastReservationDate) {
            client.lastReservationDate = new Date(dbStats.lastReservationDate);
          }
          if (dbStats.firstReservationDate) {
            client.firstReservationDate = new Date(dbStats.firstReservationDate);
          }
        }
      });
    }
    
    return baseClients;
  }, [reservations, customerNotesMap, customerStatsMap]);

  return {
    clients,
    loading,
    refetch: () => { fetchCustomerNotes(); fetchCustomerStats(); },
    customerNotesMap,
  };
}
